"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  BookOpen,
  Sparkles,
  Mic,
  ArrowRight,
  Heart,
  Compass,
  Award,
  Target,
  Users,
  Lightbulb,
  PenLine,
  Layers,
  TrendingUp,
  Newspaper,
  GraduationCap,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { TreeBlock } from "@/lib/blocks/tree-block";
import { FeatureGridBlock } from "@/lib/blocks/feature-grid-block";
import { AccordionBlock } from "@/lib/blocks/accordion-block";

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
};

const STREAMS = [
  {
    icon: PenLine,
    title: "The Operator&apos;s Notebook",
    body: "One short, disciplined note on how excellent things run, delivered every two weeks. Each issue is a complete operating mechanism — not an opinion piece, but a tool that leaders can deploy. The format follows the clinical method: the challenge diagnosed with specificity, root causes identified with forensic precision, the protocol documented with implementation clarity, and the outcome metrics defined with measurement rigor.",
    details: ["800–1,500 words per issue", "Biweekly publication rhythm", "Topics across operational leadership", "Implementable protocols, not opinions"],
    accent: "amber-gold",
  },
  {
    icon: Heart,
    title: "The 70% Project",
    body: "The flagship body of work documenting the operating case for women in leadership — not as a diversity initiative, but as an operational finding grounded in verified data from sovereign-scale deployments. The documentation spans multiple formats: essays that establish the premise, articles that present the data, longer monographs that detail the implementation method, and Notebook issues that address specific aspects in implementable form.",
    details: ["The operational case for women in leadership", "Verified data from sovereign-scale deployments", "Implementation framework, not aspiration", "Presented at the highest global stages"],
    accent: "emerald",
  },
  {
    icon: BookOpen,
    title: "Longer Essays & Monographs",
    body: "Longer-form essays and monographs that document the operating method at the depth that sustained organizational transformation requires. Reference material for organizations implementing the method, reading material for advisory and mentorship clients, and intellectual contributions to the broader discourse on operational leadership.",
    details: ["In-depth treatment of the operating method", "Reference material for advisory clients", "Intellectual contributions to leadership discourse", "Authorship: directly by Dr. Jyoti Kush"],
    accent: "purple",
  },
];

const TOPICS = [
  { title: "Operational Excellence", body: "The mechanics of execution — what excellence actually requires, how accountability systems function, why most organizations confuse aspiration with operation, and what protocols produce consistent, measurable outcomes." },
  { title: "The Clinical Method for Leadership", body: "What medicine teaches about running organizations — diagnosis before treatment, triage under pressure, protocol-driven execution, evidence over intuition, and outcome accountability. The physician&apos;s discipline as an actual operating system for leadership." },
  { title: "Women in Leadership — The 70% Evidence", body: "The operational data proving that teams composed of over 70% women produce the most reliable outcomes. The evidence without ideology — the data, the method, the implementation framework, and the verified results from deployments across eighteen countries." },
  { title: "Building in the Dark", body: "The operator&apos;s discipline in an attention economy — why discretion is a competitive advantage, how organizations perform without performing for attention, and what the discipline of compartmentalized operations produces." },
  { title: "The Triple Threat", body: "Medicine, technology, and politics as one operating method — the intersection where the most demanding problems are solved. How cross-domain thinking produces outcomes that single-domain expertise cannot achieve." },
  { title: "Governance Transformation", body: "The technology infrastructure, policy frameworks, and citizen-centric design that produce functional governance at national scale. The operational reality of building systems that serve populations at scale." },
  { title: "AI Governance & Technology Ethics", body: "The operational reality of deploying AI at sovereign scale — the ethical frameworks, the accountability structures, and the governance mechanisms that ensure technology serves organizational objectives without creating unmanaged risk." },
  { title: "Crisis Management & Organizational Resilience", body: "The clinical method for crisis response — how to triage under pressure, maintain operational continuity when systems fail, and build resilience through protocol-driven preparedness." },
];

const PRINCIPLES = [
  { num: "01", title: "Evidence Over Assertion", body: "Every claim traces to verified operational data. The discipline is the same one that governs medical practice: a physician does not prescribe without evidence.", accent: "red" },
  { num: "02", title: "Protocol Over Opinion", body: "Most leadership writing offers perspective. This writing offers protocols — specific, implementable mechanisms that organizations can deploy. Opinion is consumed and forgotten. Protocol is implemented and sustained.", accent: "purple" },
  { num: "03", title: "Diagnosis Before Prescription", body: "Every essay, article, and publication begins with a diagnostic assessment of the operational challenge it addresses. What is the problem? What are its root causes? Only after the diagnostic phase is complete does the writing prescribe.", accent: "cyan" },
  { num: "04", title: "Discretion as Discipline", body: "The writing never names clients, never reveals confidential operational details, and never compromises the compartmentalized security that governs every sovereign-level engagement.", accent: "emerald" },
  { num: "05", title: "Building, Not Performative", body: "The writing does not chase trends, respond to news cycles, or optimize for engagement metrics. It documents the long-term discipline of building organizations that sustain excellence.", accent: "amber-gold" },
];

const GAINS = [
  { icon: Layers, title: "Distributed Advisory", body: "Organizations that cannot yet engage directly still access the operating method through the publications. The Notebook delivers protocols, the 70% Project delivers the evidence base, the essays deliver the intellectual foundation.", accent: "red" },
  { icon: Users, title: "Leadership Team Alignment", body: "The writing provides a common reference framework for leadership teams seeking alignment on operating principles. The alignment is produced through sustained engagement with documented operating methods.", accent: "purple" },
  { icon: GraduationCap, title: "Executive Education Foundation", body: "The writing serves as case material and reference material for executive education programs, university lectures, and corporate training programs — bridging theory and practice in ways conventional case studies cannot.", accent: "cyan" },
  { icon: BookOpen, title: "Institutional Knowledge", body: "The writing creates institutional knowledge that persists beyond any individual engagement. When protocols are documented, distributed, and referenced, the organization acquires institutional capability that does not depend on any single individual.", accent: "emerald" },
  { icon: TrendingUp, title: "Measurable Impact", body: "Organizations that integrate the writing into their leadership development programs report measurable improvement in decision-making speed, execution consistency, and accountability discipline.", accent: "amber-gold" },
  { icon: Target, title: "Reference Material", body: "The writing serves as ongoing reference material for the operating method — implementable insights that can be distributed across leadership teams, referenced in decision-making, and used as the foundation for organizational development.", accent: "denim" },
];

export default function WritingThoughtLeadershipPage() {
  return (
    <>
      <PageHero
        icon={BookOpen}
        overline="Service · Writing & Thought Leadership"
        overlineClassName="text-amber-gold"
        title={
          <>
            Content That Shapes <span className="text-gradient-premium">Operational Thinking.</span>
          </>
        }
        subtitle="Written thought leadership that does what most leadership writing fails to do — documents the operating method with clinical precision, grounds every claim in verified operational data, and delivers implementable protocols rather than polished opinions."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "Services", href: "/services/" }, { label: "Writing & Thought Leadership" }]}
        badge="Written to be implemented"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-start">
          <div className="lg:sticky lg:top-24">
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Sparkles className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">The Thesis</span>
            </div>
            <h2 className="text-display-md mb-5">
              Written to be implemented. Not to be read.
            </h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The writing portfolio encompasses three distinct publication streams — The Operator&apos;s Notebook (a biweekly disciplined note on how excellent things run), The 70% Project (the operating case for women in leadership, documented as a body of work), and longer essays on operational excellence, the clinical method, building in the dark, and the intersection of medicine, technology, and politics.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              Each stream serves a specific purpose within the broader operating method: the biweekly publication delivers implementable protocols at frequency, the flagship project delivers the comprehensive evidence base, and the longer essays deliver the depth that sustained organizational transformation requires.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              The writing portfolio functions as a distributed advisory — organizations, leadership teams, media organizations, conference attendees, and executive education programs access the operating method through the publications. The insight that would otherwise require a direct engagement is documented, codified, and made available.
            </p>
          </div>

          <div className="space-y-3">
            {PRINCIPLES.map((p, i) => {
              const a = ACCENT[p.accent];
              return (
                <motion.div
                  key={p.num}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-30px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`size-11 rounded-xl bg-gradient-sovereign flex items-center justify-center shrink-0 ring-2 ${a.ring}`}>
                      <span className="text-caption font-bold text-white">{p.num}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{p.title}</h3>
                      <p className="text-body-sm text-muted-foreground leading-relaxed">{p.body}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <PenLine className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Three Publication Streams</span>
          </div>
          <h2 className="text-display-md mb-4">A body of work grounded in sovereign-scale experience.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Each stream serves a specific purpose within the broader operating method. Together, they constitute a comprehensive written documentation of how excellent things run.
          </p>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          {STREAMS.map((s, i) => {
            const a = ACCENT[s.accent];
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group relative glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all overflow-hidden`}
              >
                <div aria-hidden className="absolute -top-10 -right-10 size-32 rounded-full bg-current opacity-[0.05] blur-2xl group-hover:scale-150 transition-transform duration-700" />
                <div className="relative">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center mb-4 shadow-lg`}>
                    <s.icon className="size-6 text-white" />
                  </div>
                  <h3 className={`text-heading-lg mb-3 ${a.text}`}>{s.title}</h3>
                  <p className="text-body-sm text-muted-foreground leading-relaxed mb-4">{s.body}</p>
                  <ul className="space-y-1.5">
                    {s.details.map((d) => (
                      <li key={d} className={`text-caption ${a.text} font-medium flex items-start gap-1.5`}>
                        <span className="opacity-70">·</span>
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Lightbulb className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Writing Topics</span>
          </div>
          <h2 className="text-display-md mb-4">The intellectual territory.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The writing covers a defined intellectual territory — the intersection of operational excellence, clinical method, women in leadership, and building under pressure. Each topic grounded in verified experience, documented with protocol precision.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          {TOPICS.map((t, i) => {
            const accents = ["red", "purple", "cyan", "emerald", "amber-gold", "denim", "lime", "coral"];
            const a = ACCENT[accents[i % accents.length]];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.04 }}
                className={`glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <h3 className={`text-heading-md mb-2 ${a.text}`}>{t.title}</h3>
                <p className="text-caption text-muted-foreground leading-relaxed">{t.body}</p>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-4`}>
            <Award className="size-3.5 text-lime" />
            <span className="text-overline text-lime">What Organizations Gain</span>
          </div>
          <h2 className="text-display-md mb-4">Operational thinking distributed across teams.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The writing portfolio delivers value that direct engagement alone cannot achieve — the distribution of operational thinking across leadership teams, organizational layers, and institutional knowledge bases.
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {GAINS.map((g, i) => {
            const a = ACCENT[g.accent];
            return (
              <motion.div
                key={g.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-5 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className="flex items-start gap-3">
                  <div className={`size-10 rounded-lg bg-gradient-sovereign flex items-center justify-center shrink-0`}>
                    <g.icon className="size-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-heading-md mb-1.5 ${a.text}`}>{g.title}</h3>
                    <p className="text-caption text-muted-foreground leading-relaxed">{g.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-4`}>
            <PenLine className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Content Categories Tree</span>
          </div>
          <h2 className="text-display-md mb-4">Five content categories, one operating method.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The writing portfolio is organized into five root categories, each branching into specific publication types and formats. The tree structure maps what the writing covers and how each piece connects to the operating method.
          </p>
        </div>
        <TreeBlock
          entries={[
            {
              id: "w1",
              label: "Operational Excellence",
              body: "The mechanics of execution, the architecture of accountability, the discipline that produces consistent measurable outcomes.",
              accent: "red",
              children: [
                { id: "w1a", label: "How Excellence Actually Runs", body: "Essays and Notebook issues on the operating system for execution: protocols, checklists, decision frameworks, accountability structures." },
                { id: "w1b", label: "The Architecture of Discipline", body: "Long-form essays on the structural mechanisms that produce consistent outcomes under pressure — drawn from sovereign-scale experience." },
              ],
            },
            {
              id: "w2",
              label: "The Clinical Method",
              body: "What medicine teaches about running organizations — the physician's discipline as an actual operating system for leadership.",
              accent: "purple",
              children: [
                { id: "w2a", label: "Leadership as Diagnosis", body: "The translation of clinical method to organizational practice — diagnosis, triage, protocol, judgment, outcome accountability." },
                { id: "w2b", label: "From Delivery Room to Boardroom", body: "The parallel between clinical practice and operational leadership, written with the precision the parallel demands." },
              ],
            },
            {
              id: "w3",
              label: "Women in Leadership",
              body: "The 70% Project — the operational case for women in leadership, grounded in verified data from sovereign-scale deployments.",
              accent: "emerald",
              children: [
                { id: "w3a", label: "The 70% Evidence", body: "Documented findings from a decade of sovereign-scale operations proving that majority-female teams produce the most reliable outcomes." },
                { id: "w3b", label: "Building for the Talent the World Overlooks", body: "The implementation method, the recruitment strategies, the integration protocols, and the measurable outcomes." },
              ],
            },
            {
              id: "w4",
              label: "Build in the Dark",
              body: "The operator's discipline in an attention economy — discretion as a competitive advantage, not a constraint.",
              accent: "denim",
              children: [
                { id: "w4a", label: "Compartmentalized Operations", body: "How need-to-know architecture and platform-enforced isolation produce zero security incidents across sovereign-scale deployments." },
                { id: "w4b", label: "Duration Over Disruption", body: "Why long-term operational discipline outperforms the constant visibility cycle — and how to build for the former." },
              ],
            },
            {
              id: "w5",
              label: "Triple Threat",
              body: "Medicine, technology, and politics as one operating method — the intersection where the most demanding problems are solved.",
              accent: "cyan",
              children: [
                { id: "w5a", label: "Cross-Domain Integration", body: "How the intersection of clinical precision, systems architecture, and strategic discipline produces outcomes single-domain experts cannot match." },
                { id: "w5b", label: "AI Governance Through the Triple Lens", body: "Why the most demanding technology problems of the next decade require medical, technological, and political perspectives applied simultaneously." },
              ],
            },
          ]}
        />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <GraduationCap className="size-3.5 text-red" />
            <span className="text-overline text-red">How the Writing Is Used</span>
          </div>
          <h2 className="text-display-md mb-4">Applications across the engagement portfolio.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The writing portfolio serves as a distributed advisory — organizations apply the content across leadership development, board governance, advisory engagements, and operational transformation. The applications are documented and measured.
          </p>
        </div>
        <FeatureGridBlock
          features={[
            { title: "Executive Development Programs", body: "The writing serves as case material and reference material for leadership development programs. Notebook issues are assigned as pre-reading; essays become discussion frameworks; the 70% Project documentation becomes a focal reference for diversity operating model design.", accent: "red" },
            { title: "Board Reference Library", body: "Boards use the writing as reference material for governance discussions. Essays on operational excellence become reference points for strategic proposals; the writing on risk architecture informs governance design.", accent: "purple" },
            { title: "Operational Transformation Inputs", body: "The writing is distributed across leadership teams during operational transformation engagements. The content provides a common reference framework — alignment on operating principles through documented method, not through consensus meetings.", accent: "cyan" },
            { title: "Conference Reference Material", body: "Keynote and conference attendees receive curated reading lists. The writing provides post-event depth — attendees can engage with the operating method at the pace their own implementation requires.", accent: "emerald" },
            { title: "University Course Material", body: "The writing serves as course material for business schools, medical schools, and public policy programs. The clinical method for leadership is particularly effective as required reading for management and operations courses.", accent: "amber-gold" },
            { title: "Media Background Reference", body: "Press and media professionals use the writing as background reference for stories on operational excellence, women in leadership, and AI governance. The documented method provides a verified source base for editorial work.", accent: "denim" },
          ]}
        />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Lightbulb className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Frequently Asked</span>
          </div>
          <h2 className="text-display-md mb-4">Questions about the writing portfolio.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The writing portfolio spans three streams, five categories, and multiple formats. The questions below address what readers, organizations, and editors most frequently ask.
          </p>
        </div>
        <AccordionBlock
          items={[
            { q: "How often is The Operator's Notebook published?", a: "The Operator's Notebook is published biweekly — one disciplined note on how excellent things run, every two weeks. Each issue is a complete operating mechanism, not an opinion piece. The biweekly rhythm is maintained consistently regardless of operational commitments." },
            { q: "Can the writing be commissioned for specific organizational needs?", a: "Yes. Commissioned writing is available for organizations whose needs align with the operating method. Engagements include white papers for internal use, leadership development reading materials, governance transformation documentation, and editorial contributions to publication platforms. Inquiries are accepted through the contact channels." },
            { q: "Is the writing available in languages other than English?", a: "Primary publication is in English. Translations can be arranged for specific organizational needs. The writing is also available in summary form across multiple languages through The Operator's Notebook distribution network." },
            { q: "How is the writing kept current with operational developments?", a: "The writing draws from continuous operational reality — sovereign-scale operations across eighteen countries, eighteen years of experience, and ongoing engagement with the most demanding clients. The writing reflects the current state of the operating method, not archived perspectives." },
            { q: "Are essays and monographs available as standalone publications?", a: "Yes. Longer essays and monographs are published as standalone documents. The publication format varies based on the content — long-form essays, white papers, monographs, and case studies. All publications are available for organizational distribution." },
            { q: "How is the writing's impact measured?", a: "The writing's impact is measured through reader engagement, organizational adoption, and the integration of the operating method into leadership development and operational transformation programs. The Operator's Notebook subscriber base and the writing's citation in advisory and consulting engagements provide additional measurement dimensions." },
            { q: "Can organizations republish or distribute the writing internally?", a: "Yes. Organizations can distribute the writing internally under standard attribution requirements. The writing is designed for internal distribution — leadership teams, board members, advisory clients, and program participants. Specific organizational distribution agreements are available for sustained use." },
            { q: "How does the writing relate to speaking and advisory engagements?", a: "The writing functions as a distributed advisory — the same operating method that is delivered in keynote speaking, embedded in advisory engagements, and implemented in operational consulting is documented in the writing. The writing extends the engagement's reach beyond the direct engagement itself." },
          ]}
        />
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <BookOpen className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">Read. Subscribe. Inquire about writing engagements.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The Operator&apos;s Notebook, The 70% Project, and longer essays — written to be implemented. Inquiries about commissioned essays, organizational writing programs, and editorial engagements are accepted through the contact channels.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Inquire About Writing
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/services/press-media-commentary/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Newspaper className="size-4" />
                Press & Media Commentary
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "Press & Media Commentary", description: "Expert perspective for journalists and media organizations.", href: "/services/press-media-commentary/", icon: Newspaper, accent: "denim" },
          { title: "Keynote Speaking", description: "Operational authority delivered from the stage. Six signature topics.", href: "/services/keynote-speaking/", icon: Mic, accent: "red" },
          { title: "University Lectures", description: "Academic lectures for business schools and executive education programs.", href: "/services/university-lectures/", icon: GraduationCap, accent: "coral" },
          { title: "All Services", description: "Ten engagement types across six service modalities. One operating method.", href: "/services/", icon: Compass, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
